// Browser speech helpers (Web Speech API). Both are optional: the companion
// works without them, and every call degrades silently when unsupported.

export const ttsSupported = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

export const sttSupported = () =>
  typeof window !== "undefined" &&
  ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

export function listVoices() {
  if (!ttsSupported()) return [];
  try {
    return window.speechSynthesis.getVoices() || [];
  } catch (e) {
    return [];
  }
}

// Strip Markdown / action blocks so the voice reads plain sentences.
export function speakableText(text) {
  return String(text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[*_`#>]/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function speak(text, voiceName) {
  if (!ttsSupported()) return;
  const clean = speakableText(text);
  if (!clean) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(clean);
    if (voiceName) {
      const v = listVoices().find((x) => x.name === voiceName);
      if (v) u.voice = v;
    }
    u.rate = 1;
    window.speechSynthesis.speak(u);
  } catch (e) {
    /* ignore */
  }
}

export function stopSpeaking() {
  if (!ttsSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch (e) {
    /* ignore */
  }
}

/**
 * Start dictation. `onText(text, isFinal)` receives interim + final text;
 * `onEnd()` when recognition stops. Returns a stop() function (or null).
 */
export function startListening({ onText, onEnd, onError }) {
  if (!sttSupported()) return null;
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  let rec;
  try {
    rec = new Ctor();
  } catch (e) {
    return null;
  }
  rec.lang = navigator.language || "en-US";
  rec.interimResults = true;
  rec.continuous = false;
  rec.onresult = (ev) => {
    let interim = "";
    let final = "";
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const r = ev.results[i];
      if (r.isFinal) final += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (final) onText(final, true);
    else if (interim) onText(interim, false);
  };
  rec.onerror = (ev) => {
    if (onError) onError(ev && ev.error);
  };
  rec.onend = () => {
    if (onEnd) onEnd();
  };
  try {
    rec.start();
  } catch (e) {
    return null;
  }
  return () => {
    try {
      rec.stop();
    } catch (e) {
      /* ignore */
    }
  };
}
